import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogActions, List, ListItem, Switch, TextField, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import { useDispatch, useSelector } from 'store';
import { getProject } from 'store/slices/project';

const UpdateProjectDialog = ({ id, open, handleDialogClose, handleProjectUpdate }) => {
    const intl = useIntl();
    const theme = useTheme();
    const dispatch = useDispatch();

    const { project } = useSelector((state) => state.project);

    const [details, setDetails] = useState(null);

    useEffect(() => {
        if(id != null) {
            dispatch(getProject(id))
        }
    }, [id]);

    useEffect(() => {
        setDetails(project);
    }, [project]);

    const handleFieldUpdate = (field, val) => {
        const newDetails = {
            ...details,
            [field] : val
        };
        setDetails(newDetails)
    }

    const handleSubmit = () => {
        handleDialogClose();
        handleProjectUpdate(details);
    }

    return (
        <Dialog
            fullWidth
            maxWidth={'md'}
            open={open}
        >
            <DialogContent sx={{ position: 'relative' }}>
                <Close
                    sx={{
                        position: 'absolute',
                        right: 24,
                        top: 20,
                        width: 32,
                        height: 32,
                        color: '#616161',
                        cursor: 'pointer'
                    }}
                    onClick={() => handleDialogClose()}
                />
                <Typography sx={{ fontSize: '24px', fontWeight: 700, fontFamily: 'Gilroy-Bold', color: '#5A1C70', marginBottom: '40px' }}>
                    <FormattedMessage id="update-project-dialog-title" />
                </Typography>
                <Box sx={{ marginBottom: '40px' }}>
                    <Typography variant="h1" gutterBottom sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#2D2D2D', marginBottom: '8px' }}>
                        <FormattedMessage id="create-project-dialog-field-name" />
                    </Typography>
                    <TextField
                        fullWidth
                        id="projectName"
                        name="projectName"
                        placeholder={intl.formatMessage({ id: 'create-project-dialog-placeholder-name' })}
                        value={details?.name}
                        onChange={(e) => {
                            handleFieldUpdate('name', e.target.value)
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                background: '#FFFFFF',
                                fieldset: {
                                    borderColor: '#C1C9D2'
                                },
                                input: {
                                    bgcolor: '#FFFFFF'
                                },
                                '&:hover fieldset': {
                                    borderColor: '#7A2897'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#7A2897'
                                }
                            }
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '40px' }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h1" gutterBottom sx={{ fontSize: '14px', fontWeight: 700, color: '#2D2D2D', marginBottom: '8px' }}>
                            <FormattedMessage id="create-project-dialog-field-maker-private" />
                        </Typography>
                        <Typography variant="h1" gutterBottom sx={{ fontSize: '16px', fontWeight: 400, color: '#8E8E8E' }}>
                            <FormattedMessage id="create-project-dialog-field-maker-private-description" />
                        </Typography>
                    </Box>
                    <Box>
                        <Switch
                            focusVisibleClassName=".Mui-focusVisible"
                            disableRipple
                            checked={details?.isPrivate}
                            onChange={() => {
                                handleFieldUpdate('isPrivate', !details.isPrivate)
                            }}
                            sx={{
                                width: '42px',
                                height: '26px',
                                padding: 0,
                                transitionDuration: '300ms',
                                '& .MuiSwitch-switchBase': {
                                    padding: 0,
                                    margin: '2px',
                                    transitionDuration: '300ms',
                                    '&.Mui-checked': {
                                        transform: 'translateX(16px)',
                                        color: '#FFFFFF',
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
                                            opacity: 1,
                                            border: 0
                                        },
                                        '&.Mui-disabled + .MuiSwitch-track': {
                                            opacity: 0.5
                                        }
                                    },
                                    '&.Mui-focusVisible .MuiSwitch-thumb': {
                                        color: '#33CF4D',
                                        border: '6px solid #FFFFFF'
                                    },
                                    '&.Mui-disabled .MuiSwitch-thumb': {
                                        color:
                                            theme.palette.mode === 'light'
                                                ? theme.palette.grey[100]
                                                : theme.palette.grey[600]
                                    },
                                    '&.Mui-disabled + .MuiSwitch-track': {
                                        opacity: theme.palette.mode === 'light' ? 0.7 : 0.3
                                    }
                                },
                                '& .MuiSwitch-thumb': {
                                    boxSizing: 'border-box',
                                    width: 22,
                                    height: 22
                                },
                                '& span.MuiSwitch-track': {
                                    borderRadius: '13px !important',
                                    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
                                    opacity: 1,
                                    transition: theme.transitions.create(['background-color'], {
                                        duration: 500
                                    })
                                }
                            }}
                        />

                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 24px' }}>
                <Button
                    variant="contained"
                    size="large"
                    disabled={!details?.name || details?.name.trim().length < 1}
                    sx={{
                        backgroundColor: '#7A2897',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        borderRadius: '16px',
                        '&:hover': {
                            backgroundColor: '#7A2897',
                            color: '#FFFFFF'
                        }
                    }}
                    onClick={() => handleSubmit()}
                >
                    <FormattedMessage id="update-project-dialog-button-create-project" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpdateProjectDialog;
